import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import type { MouseEvent, ReactNode } from "react";
import { useState } from "react";

import { focusRing } from "../../foundations/a11y";
import { cn } from "../../lib/cn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../DropdownMenu";

import thsClientLogo from "./ths-client-logo.png";

export interface ClientNavigationTarget {
  href: string;
  label: string;
  external?: boolean;
}

export interface ClientNavigationItem extends ClientNavigationTarget {
  current?: boolean;
}

export interface ClientNavigationUser {
  displayName: string;
  email: string;
  avatarUrl?: string;
}

export type ClientNavigationContentWidth = "standard" | "wide";

export type ClientNavigationAuth =
  | { kind: "loading" }
  | { kind: "public" }
  | { kind: "signed-out"; onSignIn: () => void; signInLabel?: string }
  | {
      kind: "signed-in";
      user: ClientNavigationUser;
      onSignOut: () => void;
    };

export interface ClientNavigationBarProps {
  auth: ClientNavigationAuth;
  contentWidth?: ClientNavigationContentWidth;
  homeHref: string;
  navigationItems: ClientNavigationItem[];
  navigationLabel?: string;
  primaryAction?: ClientNavigationTarget;
  /** Personal destinations shown beside sign-out in both account menus. */
  accountItems?: ClientNavigationTarget[];
  /** A visible workspace control before the account menu, styled for navy. */
  contextControl?: ReactNode;
  onNavigate?: (href: string) => void;
}

const contentWidthClasses: Record<ClientNavigationContentWidth, string> = {
  standard: "max-w-6xl",
  wide: "max-w-7xl",
};

const navigationFocusRing =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-highlight focus-visible:ring-offset-2 focus-visible:ring-offset-navy";

function initials(displayName: string) {
  return displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function shouldUseClientNavigation(
  event: MouseEvent<HTMLAnchorElement>,
  target: ClientNavigationTarget,
  onNavigate: ClientNavigationBarProps["onNavigate"],
) {
  return Boolean(
    onNavigate &&
      !target.external &&
      !event.defaultPrevented &&
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey,
  );
}

export function ClientNavigationBar({
  auth,
  contentWidth = "wide",
  homeHref,
  navigationItems,
  navigationLabel = "Client portal",
  primaryAction,
  accountItems = [],
  contextControl,
  onNavigate,
}: ClientNavigationBarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const hasContextControl = Boolean(contextControl);

  const handleNavigation = (
    event: MouseEvent<HTMLAnchorElement>,
    target: ClientNavigationTarget,
  ) => {
    if (!shouldUseClientNavigation(event, target, onNavigate)) return;
    event.preventDefault();
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
    onNavigate?.(target.href);
  };

  return (
    <header className="relative z-30 shrink-0 bg-navy pt-[env(safe-area-inset-top)]">
      <div
        className={cn(
          "mx-auto p-4 xl:px-0",
          hasContextControl ? "md:px-6" : "md:p-6",
          contentWidthClasses[contentWidth],
        )}
      >
        <div
          className={cn(
            "grid items-center",
            hasContextControl
              ? "grid-cols-[1fr_auto] gap-x-4 gap-y-3 sm:grid-cols-[auto_minmax(0,1fr)_auto] xl:grid-cols-[auto_minmax(0,1fr)_minmax(0,18rem)_auto]"
              : "grid-cols-[1fr_auto] md:grid-cols-[0.7fr_3fr_auto] lg:grid-cols-[0.5fr_3fr_0.5fr] xl:grid-cols-[1fr_2fr_1fr]",
          )}
        >
          <div className="flex justify-start">
            <a
              href={homeHref}
              aria-label="Team Housing Solutions home"
              className={cn("rounded-sm", navigationFocusRing)}
              onClick={(event) =>
                handleNavigation(event, {
                  href: homeHref,
                  label: "Team Housing Solutions home",
                })
              }
            >
              <img className="h-10 w-auto" src={thsClientLogo} alt="THS Pro" />
            </a>
          </div>

          {auth.kind === "signed-in" ? (
            <nav
              aria-label={navigationLabel}
              className={cn(
                "col-start-2 hidden items-center justify-center",
                hasContextControl
                  ? "gap-1 whitespace-nowrap xl:flex"
                  : "gap-4 lg:flex",
              )}
            >
              {navigationItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  aria-current={item.current ? "page" : undefined}
                  onClick={(event) => handleNavigation(event, item)}
                  className={cn(
                    "rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    navigationFocusRing,
                    item.current
                      ? "bg-navy-strong text-on-navy"
                      : "text-on-navy/80 hover:bg-navy-strong hover:text-on-navy",
                  )}
                >
                  {item.label}
                </a>
              ))}
              {primaryAction ? (
                <a
                  href={primaryAction.href}
                  onClick={(event) => handleNavigation(event, primaryAction)}
                  className={cn(
                    "rounded-md bg-surface px-3.5 py-2 text-sm font-semibold text-accent transition-colors hover:bg-fill",
                    navigationFocusRing,
                  )}
                >
                  {primaryAction.label}
                </a>
              ) : null}
            </nav>
          ) : null}

          {hasContextControl ? (
            <div className="col-span-2 row-start-2 grid min-w-0 sm:col-span-1 sm:col-start-2 sm:row-start-1 sm:w-full sm:max-w-[18rem] sm:justify-self-end xl:col-start-3">
              {contextControl}
            </div>
          ) : null}

          <div
            className={cn(
              "col-start-2 row-start-1 flex items-center justify-end gap-4",
              hasContextControl
                ? "sm:col-start-3 xl:col-start-4"
                : "md:col-start-3",
            )}
          >
            {auth.kind === "signed-in" ? (
              <>
                <DropdownMenu
                  open={userMenuOpen}
                  onOpenChange={setUserMenuOpen}
                >
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label="Open user menu"
                      className={cn(
                        "hidden size-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-navy-strong text-sm font-semibold text-on-navy",
                        hasContextControl ? "xl:flex" : "lg:flex",
                        navigationFocusRing,
                      )}
                    >
                      {auth.user.avatarUrl ? (
                        <img
                          className="size-full object-cover"
                          src={auth.user.avatarUrl}
                          alt=""
                        />
                      ) : (
                        initials(auth.user.displayName) || "TH"
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-64">
                    <DropdownMenuLabel>
                      <span className="block truncate text-text">
                        {auth.user.displayName}
                      </span>
                      <span className="mt-0.5 block truncate text-body font-normal text-text-secondary">
                        {auth.user.email}
                      </span>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {accountItems.map((item) => (
                      <DropdownMenuItem key={item.href} asChild>
                        <a
                          href={item.href}
                          onClick={(event) => handleNavigation(event, item)}
                        >
                          {item.label}
                        </a>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuItem onSelect={auth.onSignOut}>
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu
                  open={mobileMenuOpen}
                  onOpenChange={setMobileMenuOpen}
                >
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label="Open main menu"
                      className={cn(
                        "flex shrink-0 rounded-md p-3 text-on-navy transition-colors hover:bg-navy-strong",
                        hasContextControl ? "xl:hidden" : "lg:hidden",
                        navigationFocusRing,
                      )}
                    >
                      {mobileMenuOpen ? (
                        <XMarkIcon className="size-6" aria-hidden="true" />
                      ) : (
                        <Bars3Icon className="size-6" aria-hidden="true" />
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    sideOffset={8}
                    className="w-[calc(100vw-2rem)] max-w-sm bg-navy p-2 text-on-navy"
                  >
                    {navigationItems.map((item) => (
                      <DropdownMenuItem
                        key={item.href}
                        asChild
                        className={cn(
                          "px-3 py-2.5 text-base text-on-navy data-[highlighted]:bg-navy-strong",
                          item.current && "bg-navy-strong",
                        )}
                      >
                        <a
                          href={item.href}
                          aria-current={item.current ? "page" : undefined}
                          onClick={(event) => handleNavigation(event, item)}
                        >
                          {item.label}
                        </a>
                      </DropdownMenuItem>
                    ))}
                    {primaryAction ? (
                      <DropdownMenuItem
                        asChild
                        className="mt-1 bg-surface px-3 py-2.5 text-base font-semibold text-accent data-[highlighted]:bg-fill"
                      >
                        <a
                          href={primaryAction.href}
                          onClick={(event) =>
                            handleNavigation(event, primaryAction)
                          }
                        >
                          {primaryAction.label}
                        </a>
                      </DropdownMenuItem>
                    ) : null}
                    {navigationItems.length || primaryAction ? (
                      <DropdownMenuSeparator className="my-2 bg-on-navy/20" />
                    ) : null}
                    <DropdownMenuLabel className="px-3 py-2 text-on-navy/70">
                      <span className="block truncate font-medium text-on-navy">
                        {auth.user.displayName}
                      </span>
                      <span className="mt-0.5 block truncate text-body font-normal text-on-navy/70">
                        {auth.user.email}
                      </span>
                    </DropdownMenuLabel>
                    {accountItems.map((item) => (
                      <DropdownMenuItem
                        key={item.href}
                        asChild
                        className="px-3 py-2.5 text-base text-on-navy data-[highlighted]:bg-navy-strong"
                      >
                        <a
                          href={item.href}
                          onClick={(event) => handleNavigation(event, item)}
                        >
                          {item.label}
                        </a>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuItem
                      className="px-3 py-2.5 text-base text-on-navy outline-none data-[highlighted]:bg-navy-strong"
                      onSelect={auth.onSignOut}
                    >
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : null}

            {auth.kind === "signed-out" ? (
              <button
                type="button"
                className={cn(
                  "rounded-md bg-surface px-3.5 py-2 text-sm font-semibold text-accent transition-colors hover:bg-fill",
                  focusRing,
                )}
                onClick={auth.onSignIn}
              >
                {auth.signInLabel ?? "Client Login"}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
}
